import React, { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  Search,
  Settings2,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { processService } from "../../services/process.service";
import useDebounce from "../../hooks/useDebounce";

const { createProcesso, deleteProcesso, updateProcesso } = processService;

const PAGE_SIZE = 10;


function ProcessoForm({ data, onChange }) {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-1.5">
        <Label>Nome do Processo</Label>
        <Input
          value={data.nome_processo}
          onChange={e => onChange({ ...data, nome_processo: e.target.value })}
          placeholder="Ex: Insalubridade Equatorial 2024"
        />
      </div>
      <div className="space-y-1.5">
        <Label>CNJ</Label>
        <Input
          value={data.cnj}
          onChange={e => onChange({ ...data, cnj: e.target.value })}
          placeholder="0000000-00.0000.0.00.0000"
          className="font-mono text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Vara</Label>
          <Input
            value={data.vara}
            onChange={e => onChange({ ...data, vara: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>UF</Label>
          <Input
            value={data.uf}
            onChange={e => onChange({ ...data, uf: e.target.value })}
            maxLength={2}
            className="uppercase"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Meta de Assinatura (%)</Label>
        <Input
          type="number"
          min={1}
          max={100}
          value={data.meta_assinatura ?? 70}
          onChange={e => onChange({ ...data, meta_assinatura: Math.min(100, Math.max(1, Number(e.target.value))) })}
          className="w-28"
        />
      </div>
    </div>
  );
}

const EMPTY_FORM = { nome_processo: "", cnj: "", vara: "", uf: "", meta_assinatura: 70 };

export function AdminProcessList({ processos, loading, onSelectProcess, onRefresh }) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);

  const [newDialog, setNewDialog] = useState(false);
  const [newData, setNewData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [editDialog, setEditDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(EMPTY_FORM);

  const [deletingId, setDeletingId] = useState(null);

  // ── filtering ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return processos.filter((p) => {
      const matchSearch =
        !q ||
        p.nome_processo?.toLowerCase().includes(q) ||
        p.cnj?.toLowerCase().includes(q) ||
        p.vara?.toLowerCase().includes(q);
      return matchSearch;
    });
  }, [processos, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetPage = () => setPage(1);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setSaving(true);
    try {
      await createProcesso(newData);
      toast.success("Processo criado com sucesso");
      setNewDialog(false);
      setNewData(EMPTY_FORM);
      onRefresh();
    } catch (e) {
      toast.error("Erro ao criar processo: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setEditData({ nome_processo: p.nome_processo || "", cnj: p.cnj || "", vara: p.vara || "", uf: p.uf || "", meta_assinatura: p.meta_assinatura ?? 70 });
    setEditDialog(true);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await updateProcesso(editingId, editData);
      toast.success("Processo atualizado");
      setEditDialog(false);
      onRefresh();
    } catch (e) {
      toast.error("Erro ao atualizar: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza? Isso apagará todos os documentos e assinantes vinculados.")) return;
    setDeletingId(id);
    try {
      await deleteProcesso(id);
      toast.success("Processo removido");
      onRefresh();
    } catch (e) {
      toast.error("Erro ao remover processo");
    } finally {
      setDeletingId(null);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); resetPage(); }}
              placeholder="Buscar por nome, CNJ ou vara…"
              className="pl-9 h-9"
            />
          </div>

        </div>

        <Button
          onClick={() => setNewDialog(true)}
          className="h-9 gap-1.5 shrink-0"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          Novo Processo
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="space-y-0">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-slate-50 last:border-0 animate-pulse">
                <div className="h-4 w-48 bg-muted rounded" />
                <div className="h-3 w-32 bg-muted rounded font-mono ml-auto hidden md:block" />
                <div className="h-3 w-20 bg-muted rounded hidden lg:block" />
                <div className="h-6 w-16 bg-muted rounded" />
                <div className="h-8 w-20 bg-muted rounded ml-4" />
              </div>
            ))}
          </div>
        ) : paged.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <FolderOpen className="w-10 h-10 text-slate-200" />
            <p className="text-sm">
              {search
                ? "Nenhum processo corresponde à busca."
                : "Nenhum processo cadastrado."}
            </p>
            {search && (
              <button
                onClick={() => { setSearch(""); resetPage(); }}
                className="text-xs text-indigo-500 hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/80">
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Processo
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                  CNJ
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                  Vara / UF
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-slate-50 last:border-0 hover:bg-muted/60 transition-colors group"
                >
                  <td className="px-5 py-3.5">
                    <p className="font-semibold text-foreground">{p.nome_processo}</p>
                  </td>
                  <td className="px-4 py-3.5 hidden md:table-cell">
                    <span className="font-mono text-xs text-muted-foreground">{p.cnj || "—"}</span>
                  </td>
                  <td className="px-4 py-3.5 text-muted-foreground hidden lg:table-cell">
                    {p.vara}{p.vara && p.uf ? " · " : ""}{p.uf}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => openEdit(p)}
                        title="Editar"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(p.id)}
                        disabled={deletingId === p.id}
                        title="Excluir"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSelectProcess(p)}
                        className="h-8 gap-1.5 border-border text-foreground hover:bg-muted ml-1"
                      >
                        <Settings2 className="w-3.5 h-3.5" />
                        Gerenciar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {filtered.length} processo{filtered.length !== 1 ? "s" : ""}
            {search ? " encontrados" : ""}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={safePage === 1}
              onClick={() => setPage(safePage - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-2 tabular-nums">{safePage} / {totalPages}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={safePage === totalPages}
              onClick={() => setPage(safePage + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog — Novo */}
      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Processo</DialogTitle>
          </DialogHeader>
          <ProcessoForm data={newData} onChange={setNewData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving || !newData.nome_processo}>
              {saving ? "Criando…" : "Criar Processo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — Editar */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Processo</DialogTitle>
          </DialogHeader>
          <ProcessoForm data={editData} onChange={setEditData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdate} disabled={saving || !editData.nome_processo}>
              {saving ? "Salvando…" : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
